import { Component, ElementRef, EventEmitter, Input, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { SavedRequest } from './network.types';
import { NETWORK_METHODS, NetworkMonitorService } from './network-monitor.service';
import { JsonViewerComponent } from '../json-viewer/json-viewer.component';

@Component({
  selector: 'app-network-saved-row',
  standalone: true,
  imports: [FormsModule, MatIconModule, JsonViewerComponent],
  host: {
    'draggable': 'true',
    '(dragstart)': 'onDragStart($event)',
    '(dragover)': 'onDragOver($event)',
    '(dragleave)': 'onDragLeave($event)',
    '(drop)': 'onDrop($event)',
    '(dragend)': 'onDragEnd()',
  },
  template: `
    <div class="row">
      @if (editingName) {
        <input class="cell name-input"
               [value]="entry.name"
               (keydown.enter)="commitName($any($event.target).value)"
               (keydown.escape)="editingName = false"
               (blur)="commitName($any($event.target).value)"
               #nameInput />
      } @else {
        <span class="cell name" [title]="entry.name" (dblclick)="startEditName()">{{ entry.name }}</span>
      }

      <select class="cell method-select" [class]="'method-' + entry.method.toLowerCase()"
              [value]="entry.method"
              (change)="onMethodChange($any($event.target).value)">
        @for (m of methods; track m) {
          <option [value]="m" [selected]="m === entry.method">{{ m }}</option>
        }
      </select>

      <input
        class="cell path-input"
        [(ngModel)]="entry.path"
        (change)="persist()"
        placeholder="/path?param=value"
      />

      <span class="cell data-cell">
        <span
          class="data-preview"
          [class.empty]="!hasBody"
          (click)="toggleSection('body')"
          [title]="hasBody ? bodyPreview : 'Add request body'"
        >{{ hasBody ? bodyPreview : '—' }}</span>
        <button class="icon-btn" (click)="toggleSection('body')" [title]="expandedSection === 'body' ? 'Collapse body' : 'Expand body'">
          <mat-icon>{{ expandedSection === 'body' ? 'expand_less' : 'expand_more' }}</mat-icon>
        </button>
        <button class="icon-btn" (click)="copyBody()" [disabled]="!hasBody" title="Copy request body">
          <mat-icon>{{ copiedField === 'body' ? 'check' : 'content_copy' }}</mat-icon>
        </button>
      </span>

      <span class="cell expand">
        <button class="icon-btn" (click)="toggleSection('response')" [disabled]="!hasLastResponse" title="Toggle last response">
          <mat-icon>{{ expandedSection === 'response' ? 'expand_less' : 'expand_more' }}</mat-icon>
        </button>
      </span>

      <span class="cell actions">
        <button class="icon-btn" (click)="relaunch.emit(entry)" title="Relaunch with current token">
          <mat-icon>play_arrow</mat-icon>
        </button>
        <button class="icon-btn" (click)="duplicate.emit(entry.id)" title="Duplicate">
          <mat-icon>content_copy</mat-icon>
        </button>
        <button class="icon-btn" (click)="toggleSection('notes')" title="Notes"
                [class.has-notes]="!!entry.notes">
          <mat-icon>{{ entry.notes ? 'sticky_note_2' : 'note_add' }}</mat-icon>
        </button>
        <button class="icon-btn danger" [class.confirming]="confirmingDelete"
                (click)="onDeleteClick()" [title]="confirmingDelete ? 'Click again to confirm' : 'Delete favorite'">
          @if (confirmingDelete) {
            <span class="confirm-text">Sure?</span>
          } @else {
            <mat-icon>delete_outline</mat-icon>
          }
        </button>
      </span>
    </div>

    @if (expandedSection === 'body') {
      <div class="section-expanded">
        <div class="section-header">
          <span class="section-label">Request body</span>
          <button class="icon-btn" (click)="copyBody()" title="Copy">
            <mat-icon>{{ copiedField === 'body' ? 'check' : 'content_copy' }}</mat-icon>
          </button>
        </div>
        <app-json-viewer [data]="entry.requestBody" mode="edit" [resizable]="true" [initialHeight]="160"
                         (save)="onBodySave($event)" />
      </div>
    }

    @if (expandedSection === 'response' && hasLastResponse) {
      <div class="section-expanded">
        <div class="section-header">
          <span class="section-label">
            Last response
            @if (entry.lastResponseStatus !== undefined) {
              <span class="status-pill" [class]="'status-' + lastResponseClass">{{ entry.lastResponseStatus }}</span>
            }
          </span>
          <button class="icon-btn" (click)="copyLastResponse()" title="Copy">
            <mat-icon>{{ copiedField === 'response' ? 'check' : 'content_copy' }}</mat-icon>
          </button>
        </div>
        <app-json-viewer [data]="entry.lastResponse" mode="view" [resizable]="true" [initialHeight]="160" />
        <div class="alias-row">
          <span class="alias-label">Save response as:</span>
          <input class="alias-input" type="text"
                 [value]="entry.responseAlias || ''"
                 placeholder="variableName"
                 (change)="onAliasChange($any($event.target).value)" />
          <span class="alias-hint">→ use as $variableName.field</span>
        </div>
      </div>
    }

    @if (expandedSection === 'notes') {
      <div class="section-expanded">
        <div class="section-header">
          <span class="section-label">Notes</span>
        </div>
        <textarea class="notes-area"
                  [value]="entry.notes || ''"
                  placeholder="Add notes about this request…"
                  (change)="onNotesChange($any($event.target).value)"
                  rows="3"></textarea>
      </div>
    }
  `,
  styles: [`
    :host {
      display: block;
      border-bottom: 1px solid #23233a;
    }

    .row {
      display: grid;
      grid-template-columns: minmax(70px, 120px) 52px minmax(0, 1.2fr) minmax(0, 1fr) 30px 90px;
      align-items: center;
      gap: 4px;
      padding: 2px 6px;
      font-size: 0.72rem;
      color: #d4d4d8;
      min-height: 26px;
    }
    .row:hover { background: #23233a; }

    .cell {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
    }

    .name {
      font-weight: 600;
      color: #fbbf24;
      cursor: default;
    }
    .name-input {
      background: #0f0f1e;
      border: 1px solid #fbbf24;
      color: #fbbf24;
      font-weight: 600;
      font-family: inherit;
      font-size: inherit;
      padding: 1px 4px;
      border-radius: 3px;
      outline: none;
      min-width: 0;
    }

    .method-select {
      background: #0f0f1e;
      border: 1px solid #3a3a5a;
      border-radius: 3px;
      font-weight: 700;
      font-size: 0.66rem;
      font-family: ui-monospace, monospace;
      text-align: center;
      padding: 1px 2px;
      cursor: pointer;
      outline: none;
      min-width: 0;
      appearance: none;
      -webkit-appearance: none;
    }
    .method-select:focus { border-color: #3b82f6; }
    .method-select option { background: #0f0f1e; color: #e5e7eb; }
    .method-get    { color: #3b82f6; }
    .method-post   { color: #16a34a; }
    .method-put    { color: #f97316; }
    .method-delete { color: #dc2626; }
    .method-patch  { color: #a855f7; }

    .path-input {
      background: #0f0f1e;
      border: 1px solid #3a3a5a;
      color: #e5e7eb;
      font-family: ui-monospace, monospace;
      font-size: 0.72rem;
      padding: 2px 6px;
      border-radius: 3px;
      outline: none;
      min-width: 0;
    }
    .path-input:focus { border-color: #3b82f6; }

    .data-cell {
      display: flex;
      gap: 1px;
      align-items: center;
      min-width: 0;
    }
    .data-preview {
      flex: 1 1 auto;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-family: ui-monospace, monospace;
      font-size: 0.68rem;
      color: #cbd5e1;
      cursor: pointer;
      padding-right: 2px;
    }
    .data-preview:hover { color: #fff; text-decoration: underline; }
    .data-preview.empty {
      color: #4b5563;
      text-align: center;
    }

    .expand {
      display: flex;
      justify-content: center;
    }

    .actions {
      display: flex;
      gap: 1px;
      align-items: center;
      justify-content: flex-end;
    }

    .icon-btn {
      background: none;
      border: none;
      color: #9ca3af;
      padding: 2px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 3px;
      flex-shrink: 0;
    }
    .icon-btn mat-icon {
      font-size: 14px;
      width: 14px;
      height: 14px;
      line-height: 14px;
    }
    .icon-btn:hover:not(:disabled) { background: #374151; color: #fff; }
    .icon-btn:disabled { opacity: 0.25; cursor: not-allowed; }
    .icon-btn.danger:hover:not(:disabled) { background: #991b1b; color: #fff; }

    /* Expanded sections (body / response) */
    .section-expanded {
      background: #0a0a18;
      padding: 4px 6px 8px 6px;
    }
    .section-header {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 2px 4px;
    }
    .section-label {
      font-size: 0.66rem;
      font-weight: 600;
      color: #94a3b8;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      flex: 1;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .status-pill {
      padding: 1px 5px;
      border-radius: 3px;
      font-family: ui-monospace, monospace;
      font-size: 0.62rem;
      color: #fff;
    }
    .status-success     { background: #16a34a; }
    .status-redirect    { background: #3b82f6; }
    .status-client-error{ background: #f97316; }
    .status-server-error{ background: #dc2626; }
    .status-error       { background: #991b1b; }

    .section-expanded app-json-viewer {
      display: block;
    }

    /* Notes */
    .notes-area {
      width: 100%;
      background: #14142b;
      border: 1px solid #334155;
      border-radius: 3px;
      color: #d4d4d8;
      font-family: inherit;
      font-size: 0.68rem;
      padding: 4px 6px;
      outline: none;
      resize: vertical;
      min-height: 40px;
      box-sizing: border-box;
    }
    .notes-area:focus { border-color: #3b82f6; }
    .icon-btn.has-notes { color: #fbbf24; }
    .icon-btn.confirming {
      background: #991b1b;
      color: #fff;
      animation: confirmPulse 0.6s ease-in-out infinite alternate;
    }
    @keyframes confirmPulse {
      from { opacity: 1; }
      to { opacity: 0.6; }
    }
    .confirm-text {
      font-size: 0.58rem;
      font-weight: 700;
      letter-spacing: 0.02em;
    }

    /* Response alias */
    .alias-row {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 4px 4px 0;
      font-size: 0.62rem;
    }
    .alias-label {
      color: #94a3b8;
      font-weight: 600;
      white-space: nowrap;
    }
    .alias-input {
      background: #14142b;
      border: 1px solid #334155;
      border-radius: 3px;
      color: #fbbf24;
      font-family: ui-monospace, monospace;
      font-size: 0.64rem;
      padding: 1px 6px;
      outline: none;
      width: 120px;
    }
    .alias-input:focus { border-color: #3b82f6; }
    .alias-hint {
      color: #4b5563;
      font-style: italic;
      white-space: nowrap;
    }

    /* Drag handle */
    :host { cursor: grab; }
    :host:active { cursor: grabbing; }
    :host.drag-over { border-top: 2px solid #3b82f6; }
  `],
})
export class NetworkSavedRowComponent {
  private _entry!: SavedRequest;
  private _prevLastResponseAt: number | null = null;

  @Input({ required: true })
  set entry(value: SavedRequest) {
    this._entry = value;
    const at = value.lastResponseAt ?? 0;
    // Only auto-expand on a NEW response (skip the very first set on mount).
    if (this._prevLastResponseAt !== null && at > this._prevLastResponseAt) {
      this.expandedSection = 'response';
    }
    this._prevLastResponseAt = at;
  }
  get entry(): SavedRequest { return this._entry; }

  @Output() relaunch = new EventEmitter<SavedRequest>();
  @Output() duplicate = new EventEmitter<string>();
  @Output() delete = new EventEmitter<string>();
  @Output() dragReorder = new EventEmitter<{ fromId: string; toId: string }>();

  private readonly monitor = inject(NetworkMonitorService);
  private readonly el = inject(ElementRef);

  readonly methods = NETWORK_METHODS;
  expandedSection: 'body' | 'response' | 'notes' | null = null;
  editingName = false;

  /** Inline delete confirmation (#61) */
  confirmingDelete = false;
  private deleteTimer: ReturnType<typeof setTimeout> | null = null;

  /** Copy visual feedback (#63) */
  copiedField: 'body' | 'response' | null = null;
  private copyTimer: ReturnType<typeof setTimeout> | null = null;

  // ── Name ──────────────────────────────────────────────────────────────────

  startEditName(): void {
    this.editingName = true;
    setTimeout(() => {
      const inp = this.el.nativeElement.querySelector('.name-input') as HTMLInputElement;
      if (inp) { inp.focus(); inp.select(); }
    });
  }

  commitName(value: string): void {
    const trimmed = value.trim();
    if (trimmed && trimmed !== this.entry.name) {
      this.entry.name = trimmed;
      this.monitor.updateFavorite(this.entry.id, { name: trimmed });
    }
    this.editingName = false;
  }

  onMethodChange(method: string): void {
    this.entry.method = method;
    this.monitor.updateFavorite(this.entry.id, { method });
  }

  // ── Body ───────────────────────────────────────────────────────────────────

  get hasBody(): boolean {
    return this.entry.requestBody !== null && this.entry.requestBody !== undefined;
  }

  get bodyPreview(): string {
    return this.preview(this.entry.requestBody);
  }

  toggleSection(section: 'body' | 'response' | 'notes'): void {
    this.expandedSection = this.expandedSection === section ? null : section;
  }

  onBodySave(payload: unknown): void {
    this.entry.requestBody = payload;
    this.persist();
  }

  copyBody(): void {
    navigator.clipboard.writeText(this.stringify(this.entry.requestBody));
    this.showCopyFeedback('body');
  }

  // ── Last response ──────────────────────────────────────────────────────────

  get hasLastResponse(): boolean {
    return this.entry.lastResponse !== null && this.entry.lastResponse !== undefined;
  }

  get lastResponseClass(): string {
    const s = this.entry.lastResponseStatus ?? 0;
    if (s >= 200 && s < 300) return 'success';
    if (s >= 300 && s < 400) return 'redirect';
    if (s >= 400 && s < 500) return 'client-error';
    if (s >= 500) return 'server-error';
    return 'error';
  }

  copyLastResponse(): void {
    navigator.clipboard.writeText(this.stringify(this.entry.lastResponse));
    this.showCopyFeedback('response');
  }

  onDeleteClick(): void {
    if (this.confirmingDelete) {
      if (this.deleteTimer) clearTimeout(this.deleteTimer);
      this.confirmingDelete = false;
      this.delete.emit(this.entry.id);
    } else {
      this.confirmingDelete = true;
      this.deleteTimer = setTimeout(() => this.confirmingDelete = false, 2000);
    }
  }

  private showCopyFeedback(field: 'body' | 'response'): void {
    if (this.copyTimer) clearTimeout(this.copyTimer);
    this.copiedField = field;
    this.copyTimer = setTimeout(() => this.copiedField = null, 1500);
  }

  // ── Persistence ────────────────────────────────────────────────────────────

  persist(): void {
    this.monitor.updateFavorite(this.entry.id, {
      path: this.entry.path,
      requestBody: this.entry.requestBody,
    });
  }

  // ── Drag & Drop ────────────────────────────────────────────────────────────

  onDragStart(event: DragEvent): void {
    // Prevent native drag ghost when dragging inside inputs or the json viewer.
    const elUnderCursor = document.elementFromPoint(event.clientX, event.clientY);
    if (elUnderCursor?.closest('input, textarea, select, .graph-container, .jv')) {
      event.preventDefault();
      return;
    }
    event.dataTransfer?.setData('text/plain', this.entry.id);
    event.dataTransfer!.effectAllowed = 'move';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'move';
    this.el.nativeElement.classList.add('drag-over');
  }

  onDragLeave(event: DragEvent): void {
    this.el.nativeElement.classList.remove('drag-over');
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.el.nativeElement.classList.remove('drag-over');
    const fromId = event.dataTransfer?.getData('text/plain');
    if (fromId && fromId !== this.entry.id) {
      this.dragReorder.emit({ fromId, toId: this.entry.id });
    }
  }

  onDragEnd(): void {
    this.el.nativeElement.classList.remove('drag-over');
  }

  onAliasChange(value: string): void {
    const trimmed = value.trim();
    this.entry.responseAlias = trimmed || undefined;
    this.monitor.updateFavorite(this.entry.id, { responseAlias: trimmed || undefined });
  }

  onNotesChange(value: string): void {
    this.entry.notes = value;
    this.monitor.updateFavorite(this.entry.id, { notes: value });
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  private preview(v: unknown): string {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'string') return v.length > 60 ? v.slice(0, 60) + '…' : v;
    try {
      const s = JSON.stringify(v);
      return s.length > 60 ? s.slice(0, 60) + '…' : s;
    } catch {
      return String(v);
    }
  }

  private stringify(v: unknown): string {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v;
    try { return JSON.stringify(v, null, 2); } catch { return String(v); }
  }
}
